import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Table, Dimmer, Loader, Segment, Header, Checkbox, Button, Icon } from 'semantic-ui-react'
import { CSVLink } from 'react-csv'
import { useZoom } from './zoomStore'

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined
  const keys = path.split('.')
  let val = obj
  for (const k of keys) {
    if (val == null) return undefined
    if (Object.prototype.hasOwnProperty.call(Object(val), k)) {
      val = val[k]
      continue
    }
    const matchingKey = Object.keys(Object(val)).find(
      key => key.toLowerCase() === k.toLowerCase()
    )
    if (!matchingKey) return undefined
    val = val[matchingKey]
  }
  return val
}

const compareAlphabetically = (left, right) => String(left ?? '').localeCompare(
  String(right ?? ''),
  'es',
  { numeric: true, sensitivity: 'base' }
)

const isDarkColor = (hex) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return false
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 0.5
}

const PageBtn = ({ page, active, label, onPageChange }) => (
  <button
    className={'SimpleTable__pageButton simple-table-pagination-button' + (active ? ' active' : '')}
    onClick={active ? undefined : () => onPageChange(page)}
    style={{
      border: active ? '2px solid #4285F4' : '1px solid #ddd',
      borderRadius: 4, padding: '2px 8px', margin: '0 2px',
      cursor: active ? 'default' : 'pointer',
      background: active ? '#e8f0fe' : '#fff',
      fontWeight: active ? 700 : 400,
      fontSize: 11, color: active ? '#000' : '#4285F4',
    }}
  >
    {label}
  </button>
)

const SimpleTable = React.memo(({
  columns = [],
  rows = [],
  loading = false,
  pagination = null,
  onPageChange = null,
  clientSidePagination = false,
  onRowClick = null,
  onDelete = null,
  serverSide = false,
  emptyMessage = 'No hay datos',
  enableExportToCSV = false,
  exportFilename = 'export.csv',
  customActions = null,
  tableId = null,
  searchValue = '',
  onSearchChange = null,
  searchPlaceholder = 'Buscar...',
  filterValues = {},
  onFilterChange = null,
  onExportReady = null,
  fetchAll = null,
  onCellChange = null,
  sortAccessor = null,
  sortOrder = null,
  onSortChange = null,
}) => {

  const zoom = useZoom()

  const [globalFilterValues, setGlobalFilterValues] = useState({})
  const [fetchingFilterValues, setFetchingFilterValues] = useState(false)
  const filterSearchTimer = useRef(null)

  const [orderedColumns, setOrderedColumns] = useState(() => {
    if (!tableId) return columns.map(c => c.accessor)
    try { const saved = localStorage.getItem(`st-order-${tableId}`); return saved ? JSON.parse(saved) : columns.map(c => c.accessor) }
    catch { return columns.map(c => c.accessor) }
  })

  const [hiddenColumns, setHiddenColumns] = useState(() => {
    if (!tableId) return []
    try { const saved = localStorage.getItem(`st-hidden-${tableId}`); return saved ? JSON.parse(saved) : [] }
    catch { return [] }
  })

  const [showColSettings, setShowColSettings] = useState(false)
  const colSettingsRef = useRef(null)
  const dragColRef = useRef(null)

  const [activeFilter, setActiveFilter] = useState(null)
  const [filterSearchText, setFilterSearchText] = useState('')
  const [localChecked, setLocalChecked] = useState({})
  const [localSort, setLocalSort] = useState(null)
  const cumulativeValues = useRef({})
  const localCheckedRef = useRef({})

  useEffect(() => { localCheckedRef.current = localChecked }, [localChecked])

  useEffect(() => {
    const fv = filterValues || {}
    const keys = Object.keys(fv)
    if (keys.length === 0) {
      setLocalChecked({})
      return
    }
    setLocalChecked(prev => {
      const next = {}
      keys.forEach(k => {
        if (fv[k] !== undefined && fv[k] !== null) {
          next[k] = fv[k]
        }
      })
      return next
    })
  }, [JSON.stringify(filterValues)])

  useEffect(() => {
    if (tableId) localStorage.setItem(`st-order-${tableId}`, JSON.stringify(orderedColumns))
  }, [orderedColumns, tableId])

  useEffect(() => {
    if (tableId) localStorage.setItem(`st-hidden-${tableId}`, JSON.stringify(hiddenColumns))
  }, [hiddenColumns, tableId])

  useEffect(() => {
    const current = columns.map(c => c.accessor)
    setOrderedColumns(prev => {
      const newCols = current.filter(c => !prev.includes(c))
      return newCols.length ? [...prev, ...newCols] : prev
    })
  }, [JSON.stringify(columns)])

  useEffect(() => {
    const handler = (e) => {
      if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
        setShowColSettings(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showColSettings])

  useEffect(() => {
    setFilterSearchText('')
  }, [activeFilter])

  const visibleColumns = useMemo(() => {
    const map = {}
    columns.forEach(c => { map[c.accessor] = c })
    return orderedColumns
      .map(a => map[a])
      .filter(c => c && (c.filterable === false || !hiddenColumns.includes(c.accessor)))
  }, [orderedColumns, hiddenColumns, JSON.stringify(columns)])

  const columnUniqueValues = useMemo(() => {
    const result = {}
    columns.forEach(col => {
      if (!col.filterable) return
      const seen = new Set()
      const vals = []
      const prev = cumulativeValues.current[col.accessor] || []
      prev.forEach(v => { const s = String(v ?? ''); if (!seen.has(s)) { seen.add(s); vals.push(v) } })
      rows.forEach(r => {
        const v = getNestedValue(r, col.accessor)
        const s = String(v ?? '')
        if (!seen.has(s)) { seen.add(s); vals.push(v) }
      })
      result[col.accessor] = vals.sort(compareAlphabetically)
    })
    if (serverSide) {
      cumulativeValues.current = { ...cumulativeValues.current, ...result }
    } else {
      cumulativeValues.current = result
    }
    return result
  }, [JSON.stringify(columns), rows])

  const currentSort = onSortChange
    ? (sortAccessor && sortOrder ? { accessor: sortAccessor, order: sortOrder } : null)
    : localSort

  const displayedRows = useMemo(() => {
    let result = rows
    if (currentSort && !onSortChange) {
      const direction = currentSort.order === 'DESC' ? -1 : 1
      result = [...rows].sort((left, right) => (
        compareAlphabetically(
          getNestedValue(left, currentSort.accessor),
          getNestedValue(right, currentSort.accessor)
        ) * direction
      ))
    }

    if (clientSidePagination && pagination) {
      const pageSize = pagination.pageSize || 25
      const page = Math.max(1, pagination.currentPage || 1)
      const start = (page - 1) * pageSize
      return result.slice(start, start + pageSize)
    }

    return result
  }, [rows, currentSort, onSortChange, clientSidePagination, pagination])

  const updateSort = (accessor, order) => {
    const nextSort = order ? { accessor, order } : null
    if (onSortChange) {
      onSortChange(nextSort)
      return
    }
    setLocalSort(nextSort)
  }

  const exportData = useMemo(() => {
    if (!enableExportToCSV) return null
    const h = visibleColumns.map(c => c.Header)
    const b = rows.map(row => visibleColumns.map(c => getNestedValue(row, c.accessor)))
    return [h, ...b]
  }, [enableExportToCSV, visibleColumns, rows])

  useEffect(() => {
    if (onExportReady) onExportReady(exportData)
  }, [exportData, onExportReady])

  const pageNumbers = useMemo(() => {
    const tp = pagination?.totalPages || 1
    const cp = pagination?.currentPage || 1
    const pages = []
    if (tp <= 7) { for (let i = 1; i <= tp; i++) pages.push(i) }
    else {
      pages.push(1)
      let s = Math.max(2, cp - 2), e = Math.min(tp - 1, cp + 2)
      if (cp <= 3) e = 5
      if (cp >= tp - 2) s = tp - 4
      if (s > 2) pages.push('...')
      for (let i = s; i <= e; i++) pages.push(i)
      if (e < tp - 1) pages.push('...')
      pages.push(tp)
    }
    return pages
  }, [pagination?.currentPage, pagination?.totalPages])

  if (loading && !rows.length) {
    return <Segment className="simple-table-loading-state" style={{ minHeight: 200 }}><Dimmer className="simple-table-loading-dimmer" active inverted><Loader>Cargando...</Loader></Dimmer></Segment>
  }

  const currentPage = pagination?.currentPage || 1
  const totalPages = pagination?.totalPages || 1
  const totalRecords = pagination?.totalRecords || rows.length
  const hasActions = !!(onRowClick || onDelete || customActions)
  const hasActiveFilters = Object.keys(filterValues).length > 0

  const toggleFilterValue = (accessor, value) => {
    if (!onFilterChange) return
    const current = localCheckedRef.current[accessor]
    const hasActiveFilter = Array.isArray(current) && current.length > 0
    const str = String(value ?? '')

    if (!hasActiveFilter) {
      setLocalChecked(prev => ({ ...prev, [accessor]: [value] }))
      onFilterChange(accessor, [value])
      setGlobalFilterValues(prev => ({ ...prev, [accessor]: undefined }))
      setFilterSearchText('')
      return
    }

    const idx = current.findIndex(s => String(s ?? '') === str)
    let next
    if (idx >= 0) {
      next = current.filter((_, i) => i !== idx)
    } else {
      next = [...current, value]
    }
    setLocalChecked(prev => ({ ...prev, [accessor]: next.length ? next : [] }))
    onFilterChange(accessor, next.length ? next : null)
    setGlobalFilterValues(prev => ({ ...prev, [accessor]: undefined }))
    setFilterSearchText('')
  }

  const clearFilters = () => {
    if (onFilterChange) { onFilterChange(null, null); setLocalChecked({}) }
  }

  const toggleColumn = (accessor) => {
    setHiddenColumns(prev => prev.includes(accessor) ? prev.filter(a => a !== accessor) : [...prev, accessor])
  }

  const resetColumns = () => {
    setOrderedColumns(columns.map(c => c.accessor))
    setHiddenColumns([])
  }

  return (
    <>
      <style>{`
        .simple-table-actions .ui.button {
          background: transparent !important;
          color: #4a6fa5 !important;
          box-shadow: none !important;
        }
        .simple-table-actions .ui.button:hover {
          background: rgba(74, 111, 165, 0.1) !important;
          color: #2c5282 !important;
        }
        .simple-table-actions .ui.red.button {
          color: #c53030 !important;
          background: transparent !important;
        }
        .simple-table-actions .ui.red.button:hover {
          background: rgba(197, 48, 48, 0.1) !important;
          color: #9b2c2c !important;
        }
      `}</style>
      {hasActiveFilters && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', marginBottom: 4 }}>
          <button onClick={clearFilters}
            className="simple-table-clear-filters"
            style={{ border: '1px solid #ef4444', borderRadius: 4, padding: '2px 10px', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>
            Limpiar Filtros ({Object.keys(filterValues).length})
          </button>
          <span style={{ fontSize: 9, color: '#888' }}>{rows.length} de {rows.length} registros</span>
        </div>
      )}
      {onSearchChange && (
        <div style={{ display: 'flex', gap: 8, padding: '2px 0 6px 0', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
            <input
              className="simple-table-search-input"
              placeholder={searchPlaceholder}
              value={searchValue || ''}
              onChange={e => onSearchChange(e.target.value)}
              style={{
                width: '100%', padding: '5px 8px 5px 26px',
                border: '1px solid #d0d0d0', borderRadius: 4,
                fontSize: 11, outline: 'none',
              }}
            />
            <span style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#999' }}>
              🔍
            </span>
          </div>
          {searchValue && (
            <button onClick={() => onSearchChange('')}
              className="simple-table-search-clear"
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, padding: 0, color: '#999' }}>
              ✕
            </button>
          )}
        </div>
      )}
      {activeFilter && columnUniqueValues[activeFilter] && (
        <div className="simple-table-filter-overlay" onClick={() => { setActiveFilter(null); if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current) }} style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.3)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="simple-table-filter-panel" onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 8, padding: '16px 20px',
            minWidth: 220, maxWidth: 360, maxHeight: '60vh', overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>
                {columns.find(c => c.accessor === activeFilter)?.Header}
              </span>
              <button onClick={() => { setActiveFilter(null); if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current) }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#999', padding: 0, lineHeight: 1 }}>
                ✕
              </button>
            </div>
            {fetchingFilterValues && (
              <div style={{ fontSize: 11, color: '#6366f1', marginBottom: 8, textAlign: 'center' }}>
                Cargando valores...
              </div>
            )}
            {(() => {
              const activeColumn = columns.find(c => c.accessor === activeFilter)
              const sortingAvailable = activeColumn?.sortable !== false && (!serverSide || onSortChange)
              if (!sortingAvailable) return null

              const isCurrentColumn = currentSort?.accessor === activeFilter
              return (
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <button onClick={() => updateSort(activeFilter, 'ASC')} style={{
                    border: '1px solid #6366f1', borderRadius: 4, padding: '2px 8px',
                    background: isCurrentColumn && currentSort.order === 'ASC' ? '#eef2ff' : '#fff',
                    color: '#4f46e5', cursor: 'pointer', fontSize: 10,
                  }}>
                    Ordenar A–Z
                  </button>
                  <button onClick={() => updateSort(activeFilter, 'DESC')} style={{
                    border: '1px solid #6366f1', borderRadius: 4, padding: '2px 8px',
                    background: isCurrentColumn && currentSort.order === 'DESC' ? '#eef2ff' : '#fff',
                    color: '#4f46e5', cursor: 'pointer', fontSize: 10,
                  }}>
                    Ordenar Z–A
                  </button>
                  {isCurrentColumn && (
                    <button onClick={() => updateSort(null, null)} style={{
                      border: 'none', background: 'none', padding: '2px 4px',
                      color: '#777', cursor: 'pointer', fontSize: 10,
                    }}>
                      Quitar orden
                    </button>
                  )}
                </div>
              )
            })()}
            <input
              className="simple-table-filter-search-input"
              placeholder="Filtrar valores..."
              value={filterSearchText}
              onChange={e => {
                const text = e.target.value
                setFilterSearchText(text)
                if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current)
                if (!activeFilter || !fetchAll || text.trim().length < 2) {
                  setGlobalFilterValues(prev => ({ ...prev, [activeFilter]: undefined }))
                  setFetchingFilterValues(false)
                  return
                }
                setFetchingFilterValues(true)
                filterSearchTimer.current = setTimeout(async () => {
                  try {
                    const allData = await fetchAll(text.trim(), true)
                    const unique = [...new Set((allData || []).map(r => getNestedValue(r, activeFilter)))].sort(compareAlphabetically)
                    setGlobalFilterValues(prev => ({ ...prev, [activeFilter]: unique }))
                  } catch (err) {
                    setGlobalFilterValues(prev => ({ ...prev, [activeFilter]: [] }))
                  } finally {
                    setFetchingFilterValues(false)
                  }
                }, 300)
              }}
              style={{
                width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4,
                fontSize: 11, outline: 'none', marginBottom: 8,
              }}
            />
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button className="simple-table-filter-action" onClick={() => {
                const allVals = columnUniqueValues[activeFilter]
                setLocalChecked(prev => ({ ...prev, [activeFilter]: allVals }))
                onFilterChange?.(activeFilter, null)
              }} style={{
                border: '1px solid #6366f1', borderRadius: 4, padding: '2px 8px',
                background: '#fff', color: '#6366f1', cursor: 'pointer', fontSize: 10,
              }}>
                Seleccionar todos
              </button>
              <button className="simple-table-filter-action" onClick={() => {
                setLocalChecked(prev => ({ ...prev, [activeFilter]: [] }))
              }} style={{
                border: '1px solid #ef4444', borderRadius: 4, padding: '2px 8px',
                background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 10,
              }}>
                Deseleccionar todos
              </button>
            </div>
            {(Array.isArray(globalFilterValues[activeFilter])
              ? globalFilterValues[activeFilter]
              : columnUniqueValues[activeFilter] || []
            )
              .filter(v => !filterSearchText || String(v ?? '').toLowerCase().includes(filterSearchText.toLowerCase()))
              .map((v, i) => {
              const vals = filterValues[activeFilter]
              const localVals = localChecked[activeFilter]
              const hasFilter = vals !== undefined && vals !== null
              const hasLocalFilter = Array.isArray(localVals)
              const str = String(v ?? '')
              const checked = hasLocalFilter
                  ? localVals.some(s => String(s ?? '') === str)
                  : hasFilter
                      ? vals.some(s => String(s ?? '') === str)
                      : true
              return (
                <label key={i} className="simple-table-filter-option" style={{
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  padding: '4px 0', fontSize: 12, borderBottom: '1px solid #f0f0f0',
                }}>
                  <Checkbox checked={checked} onChange={() => toggleFilterValue(activeFilter, v)} style={{ margin: 0 }} />
                  <span>{v == null || v === '' ? '(vacío)' : str}</span>
                </label>
              )
            })}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              {(filterValues[activeFilter] && filterValues[activeFilter].length > 0) && (
                <button className="simple-table-filter-action simple-table-filter-clear" onClick={() => {
                  setLocalChecked(prev => ({ ...prev, [activeFilter]: [] }))
                  onFilterChange?.(activeFilter, null)
                  setActiveFilter(null)
                }} style={{
                  border: '1px solid #ef4444', borderRadius: 4, padding: '4px 12px',
                  background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 11,
                }}>
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {!rows.length && (
        <>
          <Segment className="SimpleTable__empty simple-table-empty-state"><Header className="simple-table-empty-state-label" as="h4" textAlign="center" style={{ color: '#888' }}>{emptyMessage}</Header></Segment>
          {pagination && (
            <div className="simple-table-total" style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px', fontSize: 11, color: '#666' }}>
              Total: <strong style={{ marginLeft: 4 }}>{totalRecords}</strong>
            </div>
          )}
        </>
      )}
      {rows.length > 0 && (
      <>
      <div style={{
        overflowX: 'auto',
        maxHeight: displayedRows.length > 15 ? '55vh' : undefined,
          overflowY: displayedRows.length > 15 ? 'auto' : 'visible',
        }}>
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
        <Table className="SimpleTable simple-table-data-table" compact celled size="small" style={{ fontSize: 11, margin: 0, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              {hasActions && <th style={{ width: 160, textAlign: 'center', padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>Acciones</th>}
              {visibleColumns.map(col => (
                <th
                  key={col.accessor}
                  draggable
                  onDragStart={(e) => { dragColRef.current = col.accessor; e.dataTransfer.effectAllowed = 'move' }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                  onDrop={(e) => {
                    e.preventDefault()
                    const from = dragColRef.current
                    const to = col.accessor
                    if (from && from !== to) {
                      setOrderedColumns(prev => {
                        const arr = [...prev]
                        const fromIdx = arr.indexOf(from)
                        const toIdx = arr.indexOf(to)
                        if (fromIdx === -1 || toIdx === -1) return prev
                        arr.splice(fromIdx, 1)
                        arr.splice(toIdx, 0, from)
                        return arr
                      })
                    }
                    dragColRef.current = null
                  }}
                  onDragEnd={() => { dragColRef.current = null }}
                  style={{
                    width: col.width, minWidth: col.width, padding: '2px 6px', fontSize: 11,
                    cursor: 'grab', fontWeight: 700,
                    borderBottom: dragColRef.current === col.accessor ? '2px solid #4285F4' : undefined,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {col.Header}
                    {currentSort?.accessor === col.accessor && (
                      <span style={{ marginLeft: 3, color: '#4285F4', fontSize: 10 }}>
                        {currentSort.order === 'DESC' ? '↓' : '↑'}
                      </span>
                    )}
                    {col.filterable && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          const next = activeFilter === col.accessor ? null : col.accessor
                          setActiveFilter(next)
                          setFilterSearchText('')
                          if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current)
                        }}
                        style={{
                          cursor: 'pointer', fontSize: 8, marginLeft: 2,
                          color: activeFilter === col.accessor ? '#4285F4' : (filterValues[col.accessor]?.length ? '#4285F4' : '#aaa'),
                          fontWeight: (activeFilter === col.accessor || filterValues[col.accessor]?.length) ? 700 : 400,
                        }}
                      >
                        ▼
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row, ri) => (
              <tr key={row._rowKey || row.id || ri}
                style={{ backgroundColor: ri % 2 === 0 ? '#F3F7FF' : '#fff', lineHeight: 1.2 }}>
                {hasActions && (
                  <td style={{ textAlign: 'center', padding: '1px 3px', whiteSpace: 'nowrap' }} className="simple-table-actions">
                    {onRowClick && (
                      <Button icon="edit" color="blue" size="mini"
                        onClick={e => { e.stopPropagation(); onRowClick(row) }}
                        style={{ padding: 2, minWidth: 24, minHeight: 16, margin: '0 1px' }}
                      />
                    )}
                    {customActions ? customActions(row) : null}
                    {onDelete && (
                      <Button icon="trash" color="red" size="mini"
                        onClick={e => { e.stopPropagation(); onDelete(row) }}
                        style={{ padding: 2, minWidth: 24, minHeight: 16, margin: '0 1px' }}
                      />
                    )}
                  </td>
                )}
                {visibleColumns.map(col => {
                  const cellColor = typeof col.color === 'function' ? col.color(row) : null
                  const darkBg = cellColor && isDarkColor(cellColor)
                  const cellValue = getNestedValue(row, col.accessor)
                  const displayValue = col.type === 'boolean'
                    ? (cellValue ? '✓' : '✗')
                    : String(cellValue ?? '')
                  return (
                  <td key={col.accessor} title={displayValue} style={{ padding: '2px 6px', whiteSpace: 'normal', overflowWrap: 'anywhere', verticalAlign: 'top', fontSize: 11, textAlign: (col.editable && col.type === 'boolean' && onCellChange) ? 'center' : undefined, ...(cellColor ? { backgroundColor: cellColor, color: darkBg ? '#fff' : undefined, fontWeight: darkBg ? 600 : undefined } : {}) }}>
                    {col.editable && col.type === 'boolean' && onCellChange
                      ? (
                        <Checkbox
                          checked={!!getNestedValue(row, col.accessor)}
                          onChange={(e, { checked }) => onCellChange(row, col.accessor, checked)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ margin: 0 }}
                        />
                      )
                      : displayValue}
                  </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Table>
          </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {tableId && (
            <div ref={colSettingsRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button className="SimpleTable__columnsButton simple-table-column-settings-toggle" onClick={() => setShowColSettings(v => !v)}
                style={{ border: '1px solid #ddd', borderRadius: 4, padding: '3px 8px', background: '#fff', cursor: 'pointer', fontSize: 10 }}>
                Columnas
              </button>
              {showColSettings && (
                <div className="SimpleTable__columnsPanel simple-table-column-settings-panel" style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, zIndex: 100, background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: 8, minWidth: 200, maxHeight: 300, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {columns.map(col => (
                    <div key={col.accessor} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, fontSize: 10 }}>
                      <Checkbox checked={!hiddenColumns.includes(col.accessor)} onChange={() => toggleColumn(col.accessor)} style={{ margin: 0 }} />
                      <span style={{ flex: 1, fontSize: 9 }}>{col.Header}</span>
                    </div>
                  ))}
                  <button className="simple-table-column-settings-reset" onClick={resetColumns} style={{ marginTop: 4, border: '1px solid #ddd', borderRadius: 4, background: '#f5f5f5', cursor: 'pointer', padding: '2px 8px', fontSize: 9 }}>Restablecer</button>
                </div>
              )}
            </div>
          )}
          {pagination && (
            <>
              <PageBtn page={currentPage - 1} active={currentPage <= 1} label="←" onPageChange={onPageChange} />
              {pageNumbers.map((p, i) =>
                typeof p === 'number'
                  ? <PageBtn key={i} page={p} active={p === currentPage} label={p} onPageChange={onPageChange} />
                  : <span key={i} className="simple-table-pagination-ellipsis" style={{ padding: '0 4px', fontSize: 11, color: '#999' }}>...</span>
              )}
              <PageBtn page={currentPage + 1} active={currentPage >= totalPages} label="→" onPageChange={onPageChange} />
              <span className="simple-table-total" style={{ fontSize: 11, marginLeft: 10, color: '#666' }}>Total: <strong>{totalRecords}</strong></span>
            </>
          )}
        </div>
      </div>
      </>
      )}
    </>
  )
})
export default SimpleTable
